import { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";

// Spalah Crawler — серверний аналог Screaming Frog (MVP).
const TILES = [
  { key: "pages", type: "all", label: "Сторінок проскановано", sev: "info" },
  { key: "status4xx", type: "status4xx", label: "Помилки 4xx", sev: "crit" },
  { key: "status5xx", type: "status5xx", label: "Помилки 5xx", sev: "crit" },
  { key: "status3xx", type: "status3xx", label: "Редиректи 3xx", sev: "warn" },
  { key: "redirectChains", type: "redirectChains", label: "Ланцюги редиректів", sev: "warn" },
  { key: "brokenLinks", type: "brokenLinks", label: "Биті посилання", sev: "crit" },
  { key: "externalLinks", type: "external", label: "Зовнішні посилання", sev: "info" },
  { key: "nonIndexable", type: "nonindex", label: "Не індексується", sev: "warn" },
  { key: "canonicalMissing", type: "canonicalMissing", label: "Canonical відсутній", sev: "warn" },
  { key: "canonicalOther", type: "canonicalOther", label: "Canonical на іншу URL", sev: "info" },
  { key: "titleEmpty", type: "titleEmpty", label: "Title порожній", sev: "crit" },
  { key: "titleDup", type: "titleDup", label: "Title дублі", sev: "warn" },
  { key: "titleLong", type: "titleLong", label: "Title задовгий (>140)", sev: "info" },
  { key: "descEmpty", type: "descEmpty", label: "Description порожній", sev: "warn" },
  { key: "descDup", type: "descDup", label: "Description дублі", sev: "warn" },
  { key: "h1None", type: "h1None", label: "H1 відсутній", sev: "crit" },
  { key: "h1Multiple", type: "h1Multiple", label: "H1 кілька", sev: "warn" },
  { key: "dupContentPages", type: "dupContent", label: "Дублі контенту", sev: "warn" },
  { key: "imagesNoAlt", type: "imagesNoAlt", label: "Картинки без alt", sev: "warn" },
  { key: "cards", type: "cards", label: "Картки зібрано", sev: "ok" },
  { key: "filters", type: "filters", label: "Фільтри зібрано", sev: "ok" },
];
const SEV = { crit: "#ef4444", warn: "#f59e0b", info: "#6366f1", ok: "#10b981" };

export default function CrawlerView() {
  const { proj, activeId, apiFetch, t, card, btnP, dark, flash, currentUser } = useApp();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(1000);
  const [urlListText, setUrlListText] = useState("");
  const [delayMs, setDelayMs] = useState(0);
  const [crawlUA, setCrawlUA] = useState("default");
  const [crawlProxy, setCrawlProxy] = useState(false);
  const [detail, setDetail] = useState(null); // { type, label, rows, loading }
  const [queue, setQueue] = useState(null);
  const pollRef = useRef(null);

  const loadQueue = async () => {
    try { const d = await apiFetch(`/api/projects/${activeId}/crawler/queue`).then(r => r.json()); setQueue(d); } catch {}
  };

  const loadRun = async () => {
    try { const d = await apiFetch(`/api/projects/${activeId}/crawler`).then(r => r.json()); setRun(d); return d; }
    catch { return null; }
  };

  useEffect(() => {
    if (!activeId) return;
    setLoading(true); setDetail(null);
    loadRun().finally(() => setLoading(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeId]); // eslint-disable-line

  // global crawl queue — poll always (shared across projects, visible to everyone)
  useEffect(() => {
    if (!activeId) return;
    loadQueue();
    const iv = setInterval(loadQueue, 5000);
    return () => clearInterval(iv);
  }, [activeId]); // eslint-disable-line

  // poll while running
  useEffect(() => {
    if (run?.status === "running") {
      if (!pollRef.current) pollRef.current = setInterval(loadRun, 3000);
    } else if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current && run?.status !== "running") { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [run?.status]); // eslint-disable-line

  const startCrawl = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/crawler/start`, { method: "POST", body: JSON.stringify({ limit, delayMs, userAgent: crawlUA, useProxy: crawlProxy, urlList: urlListText.split(/\r?\n/).map((u) => u.trim()).filter(Boolean) }) });
      const d = await r.json();
      if (d.queued) { flash(`Додано в чергу (позиція ${d.position || "?"}) — стартує після поточного краулу`); loadQueue(); }
      else if (d.taskId) { flash("Краулінг запущено"); setRun({ status: "running", pages_crawled: 0, summary: {} }); setTimeout(loadRun, 1500); }
      else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const stopCrawl = async () => {
    try {
      await apiFetch(`/api/projects/${activeId}/crawler/stop`, { method: "POST" });
      flash("Призупиняємо… (поточні сторінки дозавантажаться)");
      setTimeout(loadRun, 1500); setTimeout(loadRun, 4000);
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const stopCurrent = async () => {
    try { await apiFetch(`/api/projects/${activeId}/crawler/stop`, { method: "POST" }); flash("Зупиняємо поточний краул…"); setTimeout(loadQueue, 1500); setTimeout(loadQueue, 4000); setTimeout(loadRun, 4000); }
    catch (e) { flash("Помилка: " + e.message); }
  };

  const resumeCrawl = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/crawler/resume`, { method: "POST" });
      const d = await r.json();
      if (d.taskId) { flash("Краулінг продовжено"); setRun(prev => ({ ...(prev || {}), status: "running" })); setTimeout(loadRun, 1500); }
      else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const openDetail = async (tile) => {
    if (!tile.type || !run?.id) return;
    setDetail({ type: tile.type, label: tile.label, rows: [], loading: true });
    try {
      const d = await apiFetch(`/api/projects/${activeId}/crawler/run/${run.id}/issue/${tile.type}`).then(r => r.json());
      setDetail({ type: tile.type, label: tile.label, rows: d.rows || [], loading: false });
    } catch (e) { setDetail({ type: tile.type, label: tile.label, rows: [], loading: false, error: e.message }); }
  };

  const downloadCsv = async (type) => {
    if (!type || !run?.id) return;
    try {
      const res = await apiFetch(`/api/projects/${activeId}/crawler/run/${run.id}/export/${type}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `crawl-${run.id}-${type}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { flash("Помилка експорту: " + (e.message || e)); }
  };

  if (!proj) return null;
  const s = run?.summary || {};
  const running = run?.status === "running";
  const paused = run?.status === "paused";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Краулер сайту</h2>
        <span style={{ fontSize: 12, color: t.textMuted }}>{proj.domain}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 11, color: t.textMuted }}>Ліміт URL</label>
          <input type="number" min={50} max={200000} step={500} value={limit} onChange={e => setLimit(parseInt(e.target.value, 10) || 1000)} title="До 200000. Для повного великого сайту (100k+) краулінг триває довго — працює у фоні."
            style={{ width: 80, fontSize: 12, padding: "5px 8px", borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#0a0a10" : "#fff", color: t.text }} disabled={running} />
          <label style={{ fontSize: 11, color: t.textMuted }} title="Пауза між запитами, мс (вічливість до сайту — щоб не банили IP). 0 = без обмеження.">Затримка, мс</label>
          <input type="number" min={0} max={2000} step={25} value={delayMs} onChange={e => setDelayMs(Math.max(0, parseInt(e.target.value, 10) || 0))} title="Пауза між запитами, мс. Для великих сайтів 25-100 мс."
            style={{ width: 64, fontSize: 12, padding: "5px 8px", borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#0a0a10" : "#fff", color: t.text }} disabled={running} />
          <label style={{ fontSize: 11, color: t.textMuted }} title="User-Agent, під яким краулер ходить по сайту. Googlebot — щоб бачити сайт очима Google (моб. чи ПК).">User-Agent</label>
          <select value={crawlUA} onChange={e => setCrawlUA(e.target.value)} disabled={running}
            style={{ fontSize: 12, padding: "5px 8px", borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#0a0a10" : "#fff", color: t.text, cursor: running ? "default" : "pointer" }}>
            <option value="default">Spalah (типово)</option>
            <option value="googlebot-mobile">Googlebot Mobile</option>
            <option value="googlebot-desktop">Googlebot ПК</option>
          </select>
          <label title="Краулити через окремий IP (Cloudflare), щоб сайт не забанив основний IP Spalah." style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: t.textMuted, cursor: running ? "default" : "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={crawlProxy} onChange={e => setCrawlProxy(e.target.checked)} disabled={running} />
            🛡 Окремий IP
          </label>
          {running ? (
            <button onClick={stopCrawl} style={{ ...btnP, fontSize: 13, background: "#f59e0b", borderColor: "#f59e0b" }}>⏸ Призупинити</button>
          ) : paused ? (
            <>
              <button onClick={resumeCrawl} style={{ ...btnP, fontSize: 13 }}>▶ Продовжити</button>
              <button onClick={startCrawl} title="Почати краул заново з нуля" style={{ fontSize: 13, padding: "6px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, cursor: "pointer" }}>↻ Заново</button>
            </>
          ) : (
            <>
            <div style={{ width: "100%", marginBottom: 8 }}>
              <label style={{ display: "block", fontSize: 11, color: t.textMuted, marginBottom: 4 }}>Список URL (по одному в рядку) — порожньо = весь сайт</label>
              <textarea value={urlListText} onChange={e => setUrlListText(e.target.value)} placeholder={"https://site.ua/page-1\nhttps://site.ua/page-2"} rows={urlListText ? 5 : 2} title="Якщо задано список — краулимо лише ці URL, без переходу по внутрішніх посиланнях." style={{ width: "100%", maxWidth: 560, fontFamily: "inherit", fontSize: 12, padding: "8px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <button onClick={startCrawl} style={{ ...btnP, fontSize: 13 }}>▶ Запустити краулінг</button>
            </>
          )}
        </div>
      </div>

      {queue && ((queue.running || []).length > 0 || (queue.queued || []).length > 0) && (
        <div style={{ ...card, padding: "12px 16px", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>🕷️ Черга краулінгу <span style={{ fontSize: 11, fontWeight: 400, color: t.textMuted }}>· спільна, 1 краул за раз</span></div>
          {(queue.running || []).map(r => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(245,158,11,0.14)", color: "#f59e0b" }}>● Краулиться</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{r.project_name}</span>
              <span style={{ fontSize: 12, color: t.textMuted }}>{Number(r.pages_crawled || 0).toLocaleString("uk-UA")} стор.</span>
              <button onClick={stopCurrent} style={{ marginLeft: "auto", fontSize: 11, padding: "4px 10px", borderRadius: 7, border: `1px solid ${t.border}`, background: "transparent", color: "#ef4444", cursor: "pointer" }}>⏸ Зупинити</button>
            </div>
          ))}
          {(queue.queued || []).map((r, i) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0", borderTop: `1px solid ${dark ? "#1e1e2c" : "#f0f0f0"}` }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: dark ? "#252535" : "#f3f4f6", color: t.textMuted }}>{i + 1} у черзі</span>
              <span style={{ fontSize: 13 }}>{r.project_name}</span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>
      ) : !run ? (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🕷️</div>
          <p style={{ fontWeight: 600, marginBottom: 6 }}>Краулінгів ще не було</p>
          <p style={{ fontSize: 13, color: t.textMuted }}>Натисніть «Запустити краулінг» — обійдемо sitemap + внутрішні посилання й зберемо технічні проблеми.</p>
        </div>
      ) : (
        <>
          <div style={{ ...card, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 8, background: (running || paused) ? "rgba(245,158,11,0.12)" : run.status === "error" ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.12)", color: (running || paused) ? "#f59e0b" : run.status === "error" ? "#ef4444" : "#10b981" }}>
              {running ? "Виконується" : paused ? "Призупинено" : run.status === "error" ? "Помилка" : "Завершено"}
            </span>
            {paused && <span style={{ fontSize: 12, color: t.textMuted }}>Краул призупинено — натисніть «Продовжити».</span>}
            <span style={{ fontSize: 12, color: t.textMuted }}>Сторінок: <strong style={{ color: t.text }}>{run.pages_crawled || 0}</strong></span>
            {run.finished_at && <span style={{ fontSize: 12, color: t.textMuted }}>{new Date(run.finished_at).toLocaleString("uk-UA")}</span>}
            {run.error && <span style={{ fontSize: 12, color: "#ef4444" }}>{run.error}</span>}
            {running && <span style={{ fontSize: 12, color: t.textMuted }}>Оновлюється автоматично…</span>}
          </div>

          {Array.isArray(s.sitemaps) && s.sitemaps.length > 0 && (
            <div style={{ ...card, padding: "12px 16px", marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>🗺️ Карти сайту знайдено: {s.sitemaps.length}</div>
              <div style={{ maxHeight: 140, overflow: "auto" }}>
                {s.sitemaps.map((sm, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, fontSize: 11.5, padding: "3px 0", color: t.textMuted }}>
                    <a href={sm.url} target="_blank" rel="noreferrer" style={{ color: "#6366f1", textDecoration: "none", flex: 1, wordBreak: "break-all" }}>{sm.url}</a>
                    <span style={{ flexShrink: 0 }}>{sm.count} URL</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10, marginBottom: 16 }}>
            {TILES.map(tile => {
              const val = s[tile.key] || 0;
              const clickable = !!tile.type && !running;
              const c = SEV[tile.sev];
              const danger = (tile.sev === "crit" || tile.sev === "warn") && val > 0;
              return (
                <div key={tile.key} onClick={() => clickable && openDetail(tile)}
                  style={{ ...card, padding: "14px 16px", cursor: clickable ? "pointer" : "default", borderLeft: `3px solid ${danger ? c : t.border}`, opacity: clickable ? 1 : 0.75 }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: danger ? c : t.text, lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: 11.5, color: t.textMuted, marginTop: 4 }}>{tile.label}</div>
                </div>
              );
            })}
          </div>

          {detail && <DetailTable detail={detail} t={t} card={card} dark={dark} onClose={() => setDetail(null)} onExport={downloadCsv} />}
        </>
      )}
    </div>
  );
}

function DetailTable({ detail, t, card, dark, onClose, onExport }) {
  const { type, label, rows, loading, error } = detail;
  const th = { textAlign: "left", padding: "8px 10px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: t.textMuted, borderBottom: `1px solid ${t.border}`, position: "sticky", top: 0, background: dark ? "#0e0e16" : "#fff" };
  const td = { padding: "8px 10px", fontSize: 12, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f3f4f6"}`, verticalAlign: "top" };
  const link = (u) => <a href={u} target="_blank" rel="noreferrer" style={{ color: "#6366f1", textDecoration: "none", wordBreak: "break-all" }}>{u}</a>;

  let head, body;
  if (type === "brokenLinks") {
    head = ["Статус", "Звідки", "Куди", "Анкор", "Тип"];
    body = rows.map((r, i) => (
      <tr key={i}><td style={{ ...td, color: "#ef4444", fontWeight: 700 }}>{r.status_code || "—"}</td><td style={td}>{link(r.from_url)}</td><td style={td}>{link(r.to_url)}</td><td style={td}>{r.anchor}</td><td style={td}>{r.is_internal ? "внутр." : "зовн."}</td></tr>
    ));
  } else if (type === "redirects" || type === "redirectChains" || type === "status3xx") {
    head = ["Знайдено на сторінці", "Посилання (редирект)", "Ланцюг переходів", "Фінальна URL"];
    const scol = (s) => (s >= 200 && s < 300) ? "#16a34a" : (s >= 300 && s < 400) ? "#f59e0b" : "#ef4444";
    body = rows.map((r, i) => (
      <tr key={i}>
        <td style={td}>
          {r.found_on ? link(r.found_on) : <span style={{ color: t.textMuted }}>— (стартова / з sitemap)</span>}
        </td>
        <td style={td}>
          {link(r.url)}
          <div style={{ fontSize: 11, fontWeight: 700, color: scol(r.status_code), marginTop: 2 }}>{r.status_code}</div>
        </td>
        <td style={td}>
          {(r.redirect_chain || []).length === 0
            ? <span style={{ color: t.textMuted, fontSize: 11 }}>—</span>
            : (r.redirect_chain || []).map((h, j) => (
              <div key={j} style={{ fontSize: 11, marginBottom: 2 }}>
                <span style={{ fontWeight: 700, color: scol(h.status) }}>{h.status}</span>
                <span style={{ color: t.textMuted }}> → </span>
                <span style={{ color: "#6366f1", wordBreak: "break-all" }}>{h.location}</span>
              </div>
            ))}
        </td>
        <td style={td}>{r.final_url ? link(r.final_url) : "—"}</td>
      </tr>
    ));
  } else if (type === "external") {
    head = ["Зовнішня URL", "Звідки (сторінка)", "Анкор", "Статус"];
    body = rows.map((r, i) => (
      <tr key={i}><td style={td}>{link(r.to_url)}</td><td style={td}>{link(r.from_url)}</td><td style={td}>{r.anchor}</td><td style={{ ...td, color: r.status_code >= 400 || r.status_code === 0 ? "#ef4444" : t.textMuted }}>{r.status_code || "—"}</td></tr>
    ));
  } else if (type === "titleDup") {
    head = ["Title (дубль)", "URL"];
    body = rows.map((r, i) => <tr key={i}><td style={td}>{r.title}</td><td style={td}>{link(r.url)}</td></tr>);
  } else if (type === "descDup") {
    head = ["Description (дубль)", "URL"];
    body = rows.map((r, i) => <tr key={i}><td style={td}>{r.description}</td><td style={td}>{link(r.url)}</td></tr>);
  } else if (type === "dupContent") {
    head = ["Hash", "URL", "Title"];
    body = rows.map((r, i) => <tr key={i}><td style={{ ...td, fontFamily: "monospace", fontSize: 10 }}>{(r.dup_key || "").slice(0, 8)}</td><td style={td}>{link(r.url)}</td><td style={td}>{r.title}</td></tr>);
  } else if (type === "nonindex") {
    head = ["URL", "Статус", "Причина"];
    body = rows.map((r, i) => <tr key={i}><td style={td}>{link(r.url)}</td><td style={td}>{r.status_code}</td><td style={td}>{r.index_reason}</td></tr>);
  } else if (type === "imagesNoAlt") {
    head = ["URL", "Без alt", "Всього img"];
    body = rows.map((r, i) => <tr key={i}><td style={td}>{link(r.url)}</td><td style={{ ...td, color: "#f59e0b", fontWeight: 700 }}>{r.images_no_alt}</td><td style={td}>{r.images_total}</td></tr>);
  } else if (type === "h1None" || type === "h1Multiple") {
    head = ["URL", "К-сть H1", "Title"];
    body = rows.map((r, i) => <tr key={i}><td style={td}>{link(r.url)}</td><td style={td}>{r.h1_count}</td><td style={td}>{r.title}</td></tr>);
  } else if (type === "canonicalMissing" || type === "canonicalOther") {
    head = ["URL", "Canonical"];
    body = rows.map((r, i) => <tr key={i}><td style={td}>{link(r.url)}</td><td style={td}>{r.canonical || "— відсутній —"}</td></tr>);
  } else if (type === "cards" || type === "filters") {
    head = [type === "cards" ? "Картка (товар)" : "Фільтр", "URL"];
    body = rows.map((r, i) => <tr key={i}><td style={td}>{r.title || <span style={{ color: t.textMuted }}>— без назви —</span>}</td><td style={td}>{link(r.url)}</td></tr>);
  } else {
    head = ["URL", "Статус", "Title", "Індекс"];
    body = rows.map((r, i) => <tr key={i}><td style={td}>{link(r.url)}</td><td style={td}>{r.status_code}</td><td style={td}>{r.title}</td><td style={{ ...td, color: r.indexable ? "#10b981" : "#f59e0b" }}>{r.index_reason}</td></tr>);
  }

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${t.border}` }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{label} <span style={{ color: t.textMuted, fontWeight: 400 }}>· {rows.length}{rows.length >= 200 ? "+" : ""}</span></div>
        {!loading && !error && rows.length > 0 && onExport && (
          <button onClick={() => onExport(type)} title="Завантажити весь список у CSV (Excel)" style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: "#6366f1", cursor: "pointer" }}>⬇ CSV</button>
        )}
        <button onClick={onClose} style={{ marginLeft: (!loading && !error && rows.length > 0 && onExport) ? 10 : "auto", background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 18 }}>×</button>
      </div>
      {loading ? <div style={{ padding: 30, textAlign: "center", color: t.textMuted, fontSize: 13 }}>Завантаження…</div>
        : error ? <div style={{ padding: 20, color: "#ef4444", fontSize: 13 }}>{error}</div>
          : rows.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#10b981", fontSize: 13 }}>✓ Проблем не знайдено</div>
            : (
              <div style={{ maxHeight: 520, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{head.map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
                  <tbody>{body}</tbody>
                </table>
              </div>
            )}
    </div>
  );
}
